class GiftWithPurchaseManager {
  constructor () {
    this.configs = []
    this.initialized = false
  }

  get debug () {
    return window.theme?.isDevTheme
  }

  log (message, ...args) {
    if (!this.debug) return
    console.info(message, ...args)
  }

  init () {
    if (this.initialized) return

    try {
      const configElements = document.querySelectorAll('[data-gwp-config]')

      if (configElements.length === 0) {
        this.log('GWP: No configuration found')
        return
      }

      this.configs = Array.from(configElements).map(configElement => ({
        enabled: configElement.dataset.gwpEnabled === 'true',
        threshold: parseInt(configElement.dataset.gwpThreshold) || 6000,
        productId: configElement.dataset.gwpProductId,
        variantId: configElement.dataset.gwpVariantId,
        campaignId: configElement.dataset.gwpCampaignId || 'default',
        autoAddEnabled: configElement.dataset.gwpAutoAddEnabled === 'true',
        showRemoveButton: configElement.dataset.gwpShowRemoveButton === 'true',
      }))

      this.log(`GWP: Initialized ${this.configs.length} campaign(s)`, this.configs.map(c => ({
        campaignId: c.campaignId,
        enabled: c.enabled,
        autoAddEnabled: c.autoAddEnabled,
        threshold: c.threshold,
        variantId: c.variantId,
      })))

      this.initialized = true
    } catch (err) {
      console.error('GWP: Init failed', err)
    }
  }

  isEnabled () {
    if (!this.initialized) this.init()
    return this.configs.length > 0 && this.configs.some(config => config.enabled && config.variantId)
  }

  process (cartState) {
    if (!cartState?.items) {
      console.warn('GWP: Invalid cart state', cartState)
      return { toAdd: [], toRemove: [] }
    }

    try {
      let cartTotal = 0
      for (const item of cartState.items) {
        if (item.properties?._gwp !== 'true') {
          cartTotal += item.final_line_price || 0
        }
      }

      const existingGifts = this.findGWPItems(cartState.items)
      const giftsToRemove = []
      const giftsToAdd = []

      for (const gift of existingGifts) {
        const giftThreshold = parseInt(gift.properties?._threshold) || 0
        const giftMaxThreshold = parseInt(gift.properties?._max_threshold) || 0
        const giftCampaign = gift.properties?._gwp_campaign || 'unknown'

        const thresholdMet = cartTotal >= giftThreshold
        const maxThresholdExceeded = giftMaxThreshold > 0 && cartTotal > giftMaxThreshold

        if (!thresholdMet) {
          this.log(`GWP: Cart below threshold for campaign "${giftCampaign}", removing gift`, gift.key)
          giftsToRemove.push(gift.key)
        } else if (maxThresholdExceeded) {
          this.log(`GWP: Cart above max threshold for campaign "${giftCampaign}", removing gift`, gift.key)
          giftsToRemove.push(gift.key)
        }
      }

      // Check if we should auto-add any gifts
      for (const config of this.configs) {
        if (!config.enabled || !config.autoAddEnabled || !config.variantId) {
          continue
        }

        // Get max threshold from the data element
        const gwpDataElement = document.querySelector(`[data-gift-with-purchase-el="opt2_data"][data-campaign-id="${config.campaignId}"]`)
        const maxThreshold = gwpDataElement ? parseInt(gwpDataElement.dataset.maxCartValue) || 999999999 : 999999999

        const thresholdMet = cartTotal >= config.threshold
        const belowMaxThreshold = cartTotal <= maxThreshold

        // Check if this exact variant is already in cart (from any campaign)
        // This prevents duplicate GWP items when multiple campaigns use the same product
        const variantInCart = cartState.items.some(item =>
          item.variant_id?.toString() === config.variantId.toString() &&
          item.properties?._gwp === 'true',
        )

        // Check if user manually removed this campaign - check cart attributes
        const manuallyRemoved = cartState.attributes?.[`gwp_removed_${config.campaignId}`] === 'true'

        // Auto-add if: threshold met, below max threshold, variant not already in cart, and not manually removed
        if (thresholdMet && belowMaxThreshold && !variantInCart && !manuallyRemoved) {
          this.log(`GWP: Auto-adding gift for campaign "${config.campaignId}"`)

          if (gwpDataElement) {
            const maxThreshold = gwpDataElement.dataset.maxCartValue || '999999999'
            const customPriceText = gwpDataElement.dataset.customPriceText || ''
            const messageLabel = gwpDataElement.dataset.productMessageLabel || ''
            const messageContent = gwpDataElement.dataset.productMessage || ''

            const properties = {
              _gwp: 'true',
              _gwp_campaign: config.campaignId,
              _gwp_auto_added: 'true',
              _threshold: config.threshold.toString(),
              _max_threshold: maxThreshold,
              _hiddenFreeGift: 'true',
              _removeEditableVariantButton: 'true',
              _customPriceText: customPriceText,
              [messageLabel]: messageContent,
            }

            if (config.showRemoveButton) {
              properties._showRemoveButton = 'true'
            } else {
              properties._hiddenFreeGiftCookieless = 'true'
            }

            giftsToAdd.push({
              id: config.variantId,
              quantity: 1,
              properties,
            })
          }
        }
      }

      return {
        toAdd: giftsToAdd,
        toRemove: giftsToRemove,
      }
    } catch (err) {
      console.error('GWP: Process error', err)
      return { toAdd: [], toRemove: [] }
    }
  }

  findGWPItems (cartItems) {
    return cartItems.filter(item => item.properties?._gwp === 'true')
  }

  getState (cartState) {
    if (!this.isEnabled()) {
      return {
        enabled: false,
        campaigns: [],
      }
    }

    const cartTotal = cartState?.total_price || 0
    const gwpItems = this.findGWPItems(cartState?.items || [])

    // Calculate state for each campaign
    const campaignStates = this.configs.map(config => {
      const threshold = config.threshold
      const thresholdMet = cartTotal >= threshold
      const remaining = Math.max(0, threshold - cartTotal)
      const progress = Math.min(100, (cartTotal / threshold) * 100)

      // Get max threshold from the data element
      const gwpDataElement = document.querySelector(`[data-gift-with-purchase-el="opt2_data"][data-campaign-id="${config.campaignId}"]`)
      const maxThreshold = gwpDataElement ? parseInt(gwpDataElement.dataset.maxCartValue) || 999999999 : 999999999
      const belowMaxThreshold = cartTotal <= maxThreshold

      // Check if this campaign has a gift in cart
      const hasGift = gwpItems.some(item =>
        item.properties?._gwp_campaign === config.campaignId,
      )

      // Check if manually removed (cart attribute)
      const wasManuallyRemoved = cartState?.attributes?.[`gwp_removed_${config.campaignId}`] === 'true'

      // Show add button logic:
      // - If auto-add is disabled: show when threshold met, below max threshold, and not in cart
      // - If auto-add is enabled: only show when threshold met, below max threshold, not in cart, AND was manually removed
      let shouldShowAddButton = false
      if (thresholdMet && belowMaxThreshold && !hasGift) {
        if (config.autoAddEnabled) {
          shouldShowAddButton = wasManuallyRemoved
        } else {
          shouldShowAddButton = true
        }
      }

      return {
        campaignId: config.campaignId,
        enabled: config.enabled,
        autoAddEnabled: config.autoAddEnabled || false,
        thresholdMet,
        progress: Math.round(progress),
        remaining,
        threshold,
        hasGift,
        wasManuallyRemoved,
        shouldShowAddButton,
      }
    })

    return {
      enabled: true,
      cartTotal,
      campaigns: campaignStates,
      totalGifts: gwpItems.length,
    }
  }

  async clearManualRemovalFlag (campaignId) {
    if (campaignId && window.BAO?.Cart) {
      await window.BAO.Cart.updateCartAttributes({
        [`gwp_removed_${campaignId}`]: '',
      })
      this.log(`GWP: Cleared manual removal attribute for campaign "${campaignId}"`)
    }
  }

  async checkCartOnLoad () {
    // Only run if auto-add is enabled for any campaign
    if (!this.configs.some(config => config.autoAddEnabled)) {
      return
    }

    try {
      // Wait for BAO.Cart to be available
      if (!window.BAO.Cart) {
        this.log('GWP: Cart not available yet, skipping initial check')
        return
      }

      const cartState = await window.BAO.Cart.getCart()
      const instructions = this.process(cartState)

      // Only process if there are gifts to add
      if (instructions.toAdd.length > 0) {
        this.log('GWP: Processing gifts on page load', instructions)

        const sections = window.BAO.sectionRenderer.pushCartSections.map(s => s.section)
        const sectionsUrl = '/'

        // Clear manual removal attributes for campaigns being re-added
        const attributesToClear = {}
        for (const item of instructions.toAdd) {
          const campaignId = item.properties._gwp_campaign
          if (campaignId) {
            attributesToClear[`gwp_removed_${campaignId}`] = ''
          }
        }
        if (Object.keys(attributesToClear).length > 0) {
          await window.BAO.Cart.updateCartAttributes(attributesToClear)
          this.log('GWP: Cleared manual removal attributes', attributesToClear)
        }

        // Add gifts through the cart with sections
        for (const item of instructions.toAdd) {
          await window.BAO.Cart.addItem(item, {
            sections,
            sectionsUrl,
            silent: true,
          })
        }

        // Refresh cart and get updated state with sections
        const updatedState = await window.BAO.Cart.refreshCart()

        // Dispatch event to update UI
        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.AFTER_ADD, {
          state: updatedState,
        })

        // Open the cart drawer
        if (window.drawers) {
          window.drawers.activeDrawerKey = 'cart'
        }
      }
    } catch (err) {
      console.error('GWP: Failed to check cart on load', err)
    }
  }
}

class GiftWithPurchaseElement extends window.BAO.CustomElement() {
  constructor () {
    super()
    this.handleTriggerClick = this.handleTriggerClick.bind(this)
  }

  connectedCallback () {
    super.connectedCallback()
    this.campaignId = this.dataset.campaignId

    this.setupTriggers()
  }

  disconnectedCallback () {
    super.disconnectedCallback()
  }

  setupTriggers () {
    const triggerButton = this.querySelector('[data-gift-with-purchase-el="drawer_trigger"]')
    if (!triggerButton) return

    const dataElement = this.querySelector('[data-gift-with-purchase-el="opt2_data"]')
    if (!dataElement) return

    this.listeners.add(triggerButton, 'click', this.handleTriggerClick)
  }

  handleTriggerClick (event) {
    event.preventDefault()

    const dataElement = this.querySelector('[data-gift-with-purchase-el="opt2_data"]')

    if (!dataElement) {
      console.warn('GWP: No data element found')
      return
    }

    const detail = {
      productUrl: dataElement.dataset.productUrl,
      productId: dataElement.dataset.productId,
      messageLabel: dataElement.dataset.productMessageLabel,
      messageContent: dataElement.dataset.productMessage,
      customPriceText: dataElement.dataset.customPriceText,
      freeGiftStyling: dataElement.dataset.freeGiftStyling,
      threshold: dataElement.dataset.minCartValue,
      maxThreshold: dataElement.dataset.maxCartValue,
      campaignId: dataElement.dataset.campaignId,
      showRemoveButton: dataElement.dataset.showRemoveButton === 'true',
      autoAddEnabled: dataElement.dataset.autoAddEnabled === 'true',
    }

    // Clear manual removal attribute when user manually adds gift
    if (this.campaignId) {
      window.BAO.GiftWithPurchase.clearManualRemovalFlag(this.campaignId)
    }

    document.dispatchEvent(new CustomEvent('addGWP', { detail }))
  }
}

if (typeof window.customElements !== 'undefined') {
  window.customElements.define('gift-with-purchase', GiftWithPurchaseElement)
}

window.BAO = window.BAO || {}
window.BAO.GiftWithPurchase = new GiftWithPurchaseManager()

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.BAO.GiftWithPurchase.init()
    window.BAO.GiftWithPurchase.checkCartOnLoad()
  })
} else {
  window.BAO.GiftWithPurchase.init()
  window.BAO.GiftWithPurchase.checkCartOnLoad()
}
